import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/site";
import { STATIC_NOTICES } from "@/lib/staticData";
import { Calendar, Award, Megaphone, Bell, ArrowRight } from "lucide-react";

const ICONS = { event: Calendar, result: Award, announcement: Megaphone, general: Bell };
const COLORS = {
  event: "from-blue-500 to-blue-700",
  result: "from-emerald-500 to-emerald-700",
  announcement: "from-gold to-amber-600",
  general: "from-royal to-royal-dark",
};

function stripYears(text) {
  if (!text) return text;
  return text
    .replace(/\b20\d{2}\s*[-–]\s*\d{2,4}\b/g, "")
    .replace(/\bfor\s+the\s+academic\s+year\b/gi, "for the upcoming academic year")
    .replace(/\(20\d{2}\)/g, "")
    .replace(/\s{2,}/g, " ")
    .trim();
}

export default function NoticesTeaser() {
  const [items, setItems] = useState([]);

  useEffect(() => {
    api.get("/notices")
      .then((r) => setItems(r.data.slice(0, 3)))
      .catch(() => setItems(STATIC_NOTICES.slice(0, 3)));
  }, []);

  if (items.length === 0) return null;

  return (
    <section className="py-20 px-6 bg-[#f5f5f5]" data-testid="notices-teaser">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-end justify-between gap-6 mb-12 flex-wrap">
          <div>
            <span className="inline-block bg-royal/5 text-royal px-5 py-1.5 rounded-full text-xs font-bold tracking-[0.25em]">NOTICE BOARD</span>
            <h2 className="font-serif-display text-4xl md:text-5xl font-bold mt-5 text-royal">Latest at Royal</h2>
            <div className="gold-divider" />
          </div>
          <Link to="/notices" data-testid="home-notices-cta" className="inline-flex items-center gap-2 text-royal font-bold hover:text-gold transition-colors">
            View all notices <ArrowRight size={16} />
          </Link>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {items.map((n, i) => {
            const Icon = ICONS[n.category] || Bell;
            const grad = COLORS[n.category] || COLORS.general;
            return (
              <Link
                key={n.id}
                to="/notices"
                data-testid={`notice-card-${i}`}
                className="group bg-white rounded-3xl overflow-hidden border border-gray-100 hover-lift block"
              >
                <div className={`h-2 bg-gradient-to-r ${grad}`} />
                <div className="p-7">
                  <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${grad} text-white flex items-center justify-center mb-4`}>
                    <Icon size={20} />
                  </div>
                  <p className="text-[11px] uppercase tracking-widest font-bold text-gold-dark">{n.category}</p>
                  <h3 className="font-serif-display text-xl font-bold text-royal-dark mt-2 group-hover:text-gold transition-colors">{stripYears(n.title)}</h3>
                  <p className="mt-3 text-gray-600 text-sm line-clamp-3">{stripYears(n.body)}</p>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
